import { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatCurrency } from '../data';
import {
  archiveProduct,
  restoreProduct,
  deleteProductPermanently,
  getAdminProducts,
  getArchivedProducts,
  getCategoriesAdmin,
  getCustomers,
  getPromotions,
  saveCategory,
  saveProduct,
  savePromotion,
  deleteCategory
} from '../services/adminManagementService';
import ProductModal from './ProductModal';

const {
  FiEdit3,
  FiPlus,
  FiTrash2,
  FiFolderPlus,
  FiArchive,
  FiRefreshCw,
  FiAlertTriangle
} = FiIcons;

const productDefaults = {
  name: '',
  slug: '',
  description: '',
  price: '',
  category_id: '',
  status: 'active',
  featured: false,
  quantity_on_hand: 10,
  low_stock_threshold: 2,
  media: [],
  colors: []
};

export function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [archived, setArchived] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  const load = async () => {
    setLoading(true);
    try {
      const [prodData, archData, catData] = await Promise.all([
        getAdminProducts(),
        getArchivedProducts(),
        getCategoriesAdmin()
      ]);
      setProducts(prodData);
      setArchived(archData);
      setCategories(catData);
    } catch {
      setMessage('Failed to load products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (values) => {
    await saveProduct(values);
    await load();
  };

  const handleCreateCategory = async (name) => {
    const slug = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    await saveCategory({
      name,
      slug,
      description: '',
      active: true,
      sort_order: categories.length + 1
    });

    const refreshed = await getCategoriesAdmin();
    setCategories(refreshed);
    return refreshed.find((c) => c.slug === slug);
  };

  const handleArchive = async (product) => {
    if (!window.confirm(`Archive "${product.name}"? It will move to the Archive tab.`)) return;
    try {
      await archiveProduct(product.id);
      setMessage(`"${product.name}" has been archived.`);
      await load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleRestore = async (product) => {
    if (!window.confirm(`Restore "${product.name}"? It will return to the active products list.`)) return;
    try {
      await restoreProduct(product.id);
      setMessage(`"${product.name}" has been restored.`);
      await load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleDeleteForever = async (product) => {
    if (
      !window.confirm(
        `PERMANENTLY DELETE "${product.name}"?\n\nThis cannot be undone. All associated media, variants, and inventory will also be removed.`
      )
    ) return;
    try {
      await deleteProductPermanently(product.id);
      setMessage(`"${product.name}" has been permanently deleted.`);
      await load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const isArchiveTab = activeTab === 'archive';
  const displayList = isArchiveTab ? archived : products;

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Products &amp; Stock</h1>
          <p className="admin-page-subtitle">
            Manage your saree collection, prices, colors, and stock.
          </p>
        </div>
        {!isArchiveTab && (
          <button
            className="admin-btn-primary"
            onClick={() => setEditing({ ...productDefaults })}
          >
            <SafeIcon icon={FiPlus} />
            Add New Saree
          </button>
        )}
      </div>

      {/* Tab Switcher */}
      <div className="products-tab-bar">
        <button
          className={`products-tab ${!isArchiveTab ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <SafeIcon icon={FiPlus} />
          <span>Active Products</span>
          <span className="products-tab-count">{products.length}</span>
        </button>
        <button
          className={`products-tab ${isArchiveTab ? 'active' : ''}`}
          onClick={() => setActiveTab('archive')}
        >
          <SafeIcon icon={FiArchive} />
          <span>Archive</span>
          <span className="products-tab-count">{archived.length}</span>
        </button>
      </div>

      {message && <div className="admin-alert info">{message}</div>}

      {loading ? (
        <div className="admin-loading-state">Loading products…</div>
      ) : displayList.length === 0 ? (
        <div className="admin-empty-state">
          {isArchiveTab
            ? 'No archived products.'
            : 'No products found. Click "Add New Saree" to start.'}
        </div>
      ) : (
        <div className="admin-product-table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Colors</th>
                <th>Stock</th>
                {isArchiveTab ? <th>Archived</th> : <th>Status</th>}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayList.map((p) => {
                const primaryMedia =
                  p.media?.find((m) => m.is_primary) || p.media?.[0];
                const stock = p.inventory?.[0];
                const available = Math.max(
                  0,
                  Number(stock?.quantity_on_hand || 0)
                    - Number(stock?.quantity_reserved || 0)
                    - Number(stock?.quantity_committed || 0)
                );
                const colorCount =
                  p.variants?.filter(
                    (v) => v.color || v.attributes?.color
                  ).length || 0;

                return (
                  <tr key={p.id}>
                    <td>
                      <div className="product-cell">
                        {primaryMedia?.media_url ? (
                          <img src={primaryMedia.media_url} alt={p.name} />
                        ) : (
                          <div className="img-placeholder">ZEVI</div>
                        )}
                        <div>
                          <strong>{p.name}</strong>
                          <small>{p.media?.length || 0} media</small>
                        </div>
                      </div>
                    </td>
                    <td>{p.category?.name || 'Uncategorized'}</td>
                    <td><strong>{formatCurrency(p.price)}</strong></td>
                    <td>
                      <span className="stock-badge">
                        {colorCount} color{colorCount !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`stock-badge ${
                          available === 0
                            ? 'out'
                            : available <= 2
                              ? 'low'
                              : 'ok'
                        }`}
                      >
                        {available} units
                      </span>
                    </td>
                    {isArchiveTab ? (
                      <td>
                        <small style={{ color: 'var(--admin-muted)' }}>
                          {p.archived_at
                            ? new Date(p.archived_at).toLocaleDateString()
                            : '—'}
                        </small>
                      </td>
                    ) : (
                      <td>
                        <span className={`status-pill ${p.status}`}>
                          {p.status}
                        </span>
                      </td>
                    )}
                    <td className="text-right">
                      <div className="table-btn-group">
                        {isArchiveTab ? (
                          <>
                            <button
                              className="table-action-btn edit"
                              onClick={() => handleRestore(p)}
                              title="Restore to active"
                            >
                              <SafeIcon icon={FiRefreshCw} />
                              <span>Restore</span>
                            </button>
                            <button
                              className="table-action-btn delete-permanent"
                              onClick={() => handleDeleteForever(p)}
                              title="Delete permanently"
                            >
                              <SafeIcon icon={FiAlertTriangle} />
                              <span>Delete</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="table-action-btn edit"
                              onClick={() => setEditing(p)}
                            >
                              <SafeIcon icon={FiEdit3} />
                              <span>Edit</span>
                            </button>
                            {p.status !== 'archived' && (
                              <button
                                className="table-action-btn delete"
                                onClick={() => handleArchive(p)}
                                title="Archive this product"
                              >
                                <SafeIcon icon={FiTrash2} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProductModal
          categories={categories}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onCreateCategory={handleCreateCategory}
        />
      )}
    </div>
  );
}

export function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await getCategoriesAdmin();
      setCategories(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const slug = name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const payload = {
        name,
        slug,
        description,
        active: true,
        sort_order: categories.length + 1
      };
      if (editing) {
        payload.id = editing.id;
        payload.sort_order = editing.sort_order;
      }
      await saveCategory(payload);
      setName('');
      setDescription('');
      setModalOpen(false);
      setEditing(null);
      setMessage(editing ? 'Category updated.' : 'Category saved.');
      load();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cat) => {
    setEditing(cat);
    setName(cat.name);
    setDescription(cat.description || '');
    setModalOpen(true);
  };

  const handleDelete = async (cat) => {
    if (
      !window.confirm(
        `Delete category "${cat.name}"? This will not delete products.`
      )
    ) return;
    try {
      await deleteCategory(cat.id);
      setMessage(`Category "${cat.name}" deleted.`);
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const openNew = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setModalOpen(true);
  };

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Categories</h1>
          <p className="admin-page-subtitle">
            Organise sarees into customer-friendly collections.
          </p>
        </div>
        <button className="admin-btn-primary" onClick={openNew}>
          <SafeIcon icon={FiFolderPlus} />
          Add Category
        </button>
      </div>

      {message && <div className="admin-alert info">{message}</div>}

      {loading ? (
        <div className="admin-loading-state">Loading categories…</div>
      ) : categories.length === 0 ? (
        <div className="admin-empty-state">
          No categories yet. Create your first one.
        </div>
      ) : (
        <div className="admin-category-grid">
          {categories.map((cat) => (
            <div className="admin-category-card" key={cat.id}>
              <div className="category-card-top">
                <h3>{cat.name}</h3>
                <span className="category-status-pill">
                  {cat.active ? 'Active' : 'Hidden'}
                </span>
              </div>
              <p className="category-desc">
                {cat.description || 'No description provided.'}
              </p>
              <div className="category-card-footer">
                <small>
                  Slug: <code>{cat.slug}</code>
                </small>
                <div className="category-card-actions">
                  <button
                    className="table-action-btn edit"
                    onClick={() => handleEdit(cat)}
                  >
                    <SafeIcon icon={FiEdit3} /> Edit
                  </button>
                  <button
                    className="table-action-btn delete"
                    onClick={() => handleDelete(cat)}
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card small">
            <div className="modal-header">
              <div>
                <span className="modal-eyebrow">
                  {editing ? 'EDIT CATEGORY' : 'NEW CATEGORY'}
                </span>
                <h2>
                  {editing ? `Edit: ${editing.name}` : 'Add Category'}
                </h2>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setModalOpen(false);
                  setEditing(null);
                }}
              >
                <SafeIcon icon={FiPlus} />
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              <label className="admin-field-label">
                Category Name *
                <input
                  required
                  placeholder="e.g. Silk Sarees"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="admin-field-label">
                Description
                <textarea
                  rows="3"
                  placeholder="Brief description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <div className="modal-footer">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setModalOpen(false);
                    setEditing(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving…'
                    : editing
                      ? 'Update Category'
                      : 'Save Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [code, setCode] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setPromos(await getPromotions());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!code || !discountValue) return;
    await savePromotion({
      code,
      discount_value: discountValue,
      discount_type: 'percentage',
      active: true,
      description: `Promo discount ${discountValue}%`
    });
    setCode('');
    setDiscountValue('');
    setModalOpen(false);
    load();
  };

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Discount Promo Codes</h1>
          <p className="admin-page-subtitle">
            Create discount coupons for customers.
          </p>
        </div>
        <button
          className="admin-btn-primary"
          onClick={() => setModalOpen(true)}
        >
          <SafeIcon icon={FiPlus} />
          Create Coupon
        </button>
      </div>

      <div className="admin-product-table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Coupon Code</th>
              <th>Discount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.code}</strong></td>
                <td>
                  {p.discount_type === 'percentage'
                    ? `${p.discount_value}% OFF`
                    : formatCurrency(p.discount_value)}
                </td>
                <td>
                  <span className="status-pill active">
                    {p.active ? 'Active' : 'Disabled'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card small">
            <div className="modal-header">
              <h2>New Discount Code</h2>
              <button
                className="modal-close-btn"
                onClick={() => setModalOpen(false)}
              >
                <SafeIcon icon={FiPlus} />
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              <label className="admin-field-label">
                Code Name *
                <input
                  required
                  placeholder="e.g. ZEVI10"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              <label className="admin-field-label">
                Percentage (%) *
                <input
                  type="number"
                  min="1"
                  max="100"
                  required
                  placeholder="10"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              </label>
              <div className="modal-footer">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn-primary">
                  Save Code
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCustomers().then(setCustomers).finally(() => setLoading(false));
  }, []);

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Customers</h1>
          <p className="admin-page-subtitle">
            Customer contacts from active orders.
          </p>
        </div>
      </div>

      <div className="admin-product-table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Registered</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.full_name}</strong></td>
                <td>{c.email}</td>
                <td><a href={`tel:${c.phone}`}>{c.phone}</a></td>
                <td>{new Date(c.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}